import { AdminLayout } from "@/views/layouts/AppLayout";
import { TradeOrderPage } from "@/views/pages/tradeManage/TradeOrderPage";
import { TradeOrderDetailPage } from "@/views/pages/tradeManage/TradeOrderDetailPage";
import { TradeOrderAuditPage } from "@/views/pages/tradeManage/TradeOrderAuditPage";
import { TradeInvoicePage } from "@/views/pages/tradeManage/TradeInvoicePage";
import { TradeInvoiceDetailPage } from "@/views/pages/tradeManage/TradeInvoiceDetailPage";
import { TradeInvoiceFormPage } from "@/views/pages/tradeManage/TradeInvoiceFormPage";
import { TradeOrderRecordFormPage } from "@/views/pages/tradeManage/TradeOrderRecordFormPage";
import { TradeCollectionPage } from "@/views/pages/tradeManage/TradeCollectionPage";
import { TradeCollectionDetailPage } from "@/views/pages/tradeManage/TradeCollectionDetailPage";
import { TradeRefundPage } from "@/views/pages/tradeManage/TradeRefundPage";
import { TradeRefundDetailPage } from "@/views/pages/tradeManage/TradeRefundDetailPage";
import { TradeRefundFormPage } from "@/views/pages/tradeManage/TradeRefundFormPage";
import { ZoneAccountPage } from "@/views/pages/tradeManage/ZoneAccountPage";
import { ZoneRechargeDetailPage } from "@/views/pages/tradeManage/ZoneRechargeDetailPage";
import { ZoneReservationOrderPage } from "@/views/pages/tradeManage/ZoneReservationOrderPage";
import { ZoneReservationOrderDetailPage } from "@/views/pages/tradeManage/ZoneReservationOrderDetailPage";
import { ZoneReservationOrderEditPage } from "@/views/pages/tradeManage/ZoneReservationOrderEditPage";
import { ZoneContractPage } from "@/views/pages/tradeManage/zoneContract/ZoneContractPage";
import { ZoneContractFormPage } from "@/views/pages/tradeManage/zoneContract/ZoneContractFormPage";
import { ZoneContractDetailPage } from "@/views/pages/tradeManage/zoneContract/ZoneContractDetailPage";
import { EnterpriseManagePage } from "@/views/pages/tradeManage/enterpriseManage/EnterpriseManagePage";
import { SupplierDetailPage } from "@/views/pages/supplierManagement/SupplierDetailPage";
import { TradeOrderDeliveryDetailPage } from "@/views/pages/tradeManage/TradeOrderDeliveryDetailPage";
import type { AdminRouteObject } from "../index";

/** 专区合同相关路由共用 Vue 的四项权限，包含单价调整能力。 */
const zoneContractPermissions = [
    "ROLE_ZONE_CONTRACT_R",
    "ROLE_ZONE_CONTRACT_DEAL",
    "ROLE_ZONE_CONTRACT_MANAGE",
    "ROLE_ZONE_CONTRACT_PRICE_ADJUST"
];

/** 交易管理路由，先接入交易订单首屏并保留 Vue 权限集合。 */
export const tradeManageRoutes: AdminRouteObject = {
    path: "/tradeManage",
    element: <AdminLayout />,
    handle: { title: "交易管理" },
    children: [
        {
            // 首轮只接入企业管理列表最小闭环，沿用 Vue 供应商权限。
            path: "enterpriseManage",
            element: <EnterpriseManagePage />,
            handle: {
                // Vue 旧版该入口实际复用供应商管理菜单，进入交易历史地址时也保持供应商导航高亮。
                title: "供应商管理",
                activeMenu: "/supplierManagement/enterpriseManage",
                permissions: ["ROLE_SUPPLIER_R", "ROLE_SUPPLIER_DEAL"]
            }
        },
        {
            // 详情编号沿用 Vue 历史 query.id 传递。
            path: "enterpriseManage/detail",
            // 旧版企业详情实际复用供应商详情模板，兼容入口也复用同一 React 页面保证视觉一致。
            element: <SupplierDetailPage />,
            handle: { title: "企业详情", permissions: ["ROLE_SUPPLIER_R", "ROLE_SUPPLIER_DEAL"] }
        },
        {
            path: "tradeOrder",
            element: <TradeOrderPage />,
            handle: {
                title: "交易订单",
                permissions: [
                    "ROLE_TRADE_ORDER_R",
                    "ROLE_TRADE_ORDER_MANAGE",
                    "ROLE_TRADE_ORDER_DELETE",
                    "ROLE_TRADE_ORDER_DEAL",
                    "ROLE_TRADE_ORDER_EXPORT"
                ]
            }
        },
        {
            path: "tradeOrder/detail",
            element: <TradeOrderDetailPage />,
            handle: {
                // 旧 Vue 详情路由在顶部标签中显示“订单详情”，页面标题仍由详情头单独展示。
                title: "订单详情",
                // 详情页还承载订单处理、删除等旧 Vue 操作，直达路由需保留完整权限集合。
                permissions: [
                    "ROLE_TRADE_ORDER_R",
                    "ROLE_TRADE_ORDER_MANAGE",
                    "ROLE_TRADE_ORDER_DELETE",
                    "ROLE_TRADE_ORDER_DEAL"
                ]
            }
        },
        {
            path: "invoiceManage",
            element: <TradeInvoicePage />,
            handle: {
                title: "发票管理",
                // 发票列表包含作废等处理动作，沿用旧 Vue 的查看和处理权限。
                permissions: ["ROLE_INVOICE_R", "ROLE_INVOICE_DEAL"]
            }
        },
        {
            // 沿用 Vue 收款管理入口和三组可访问权限，首屏列表先独立迁移。
            path: "collectionManage",
            element: <TradeCollectionPage />,
            handle: {
                title: "收款管理",
                permissions: ["ROLE_RECEIVABLES_R", "ROLE_RECEIVABLES_DEAL", "ROLE_RECEIVABLES_MANAGE"]
            }
        },
        {
            // 沿用 Vue 收款详情隐藏入口和同一组权限，详情通过 query.id 加载收款单。
            path: "collection/detail",
            element: <TradeCollectionDetailPage />,
            handle: {
                title: "收款详情",
                permissions: ["ROLE_RECEIVABLES_R", "ROLE_RECEIVABLES_DEAL", "ROLE_RECEIVABLES_MANAGE"]
            }
        },
        {
            // 沿用 Vue 退款管理入口和读、管理、处理三组权限，先接入首屏列表。
            path: "refundManage",
            element: <TradeRefundPage />,
            handle: {
                title: "退款管理",
                permissions: ["ROLE_REFUND_R", "ROLE_REFUND_MANAGE", "ROLE_REFUND_DEAL"]
            }
        },
        {
            // 沿用 Vue 退款详情隐藏入口，详情通过 query.id 加载退款记录。
            path: "refund/detail",
            element: <TradeRefundDetailPage />,
            handle: {
                title: "退款详情",
                // 详情直达入口在旧 Vue 中仅需要查看权限，审核由独立 audit 路由控制。
                permissions: ["ROLE_REFUND_R"]
            }
        },
        {
            // 沿用 Vue 独立退款审核入口，审核页复用详情数据和审核弹窗。
            path: "refund/audit",
            element: <TradeRefundDetailPage />,
            handle: { title: "审核退款", permissions: ["ROLE_REFUND_MANAGE", "ROLE_REFUND_R"] }
        },
        {
            // 沿用 Vue 退款新增历史入口，表单通过 query.id 区分新增和编辑。
            path: "refund/add",
            element: <TradeRefundFormPage />,
            handle: { title: "新增退款", permissions: ["ROLE_REFUND_DEAL"] }
        },
        {
            // 沿用 Vue 退款编辑历史入口，复用同一表单并保持处理权限。
            path: "refund/edit",
            element: <TradeRefundFormPage />,
            handle: { title: "编辑退款", permissions: ["ROLE_REFUND_DEAL"] }
        },
        {
            // 保留 Vue 发票详情历史入口，详情页按 query 参数读取发票编号。
            path: "invoiceDetail",
            element: <TradeInvoiceDetailPage />,
            handle: { title: "发票详情", permissions: ["ROLE_INVOICE_R"] }
        },
        {
            // 保留 Vue 发票新增历史入口，表单提交沿用发票接口和权限标识。
            path: "invoiceAdd",
            element: <TradeInvoiceFormPage />,
            handle: { title: "新增发票", permissions: ["ROLE_INVOICE_DEAL"] }
        },
        {
            // 使用 Vue 的新增入口，避免 React 额外暴露不存在的 /tradeOrder/form 路由。
            path: "tradeOrder/add",
            element: <TradeOrderRecordFormPage />,
            handle: {
                title: "新增补录订单",
                permissions: [
                    "ROLE_TRADE_ORDER_R",
                    "ROLE_TRADE_ORDER_MANAGE",
                    "ROLE_TRADE_ORDER_DELETE",
                    "ROLE_TRADE_ORDER_DEAL"
                ]
            }
        },
        {
            // 保留 Vue 版本编辑入口，复用同一补录订单表单并读取 id query 参数。
            path: "tradeOrder/edit",
            element: <TradeOrderRecordFormPage />,
            handle: {
                title: "编辑补录订单",
                permissions: [
                    "ROLE_TRADE_ORDER_R",
                    "ROLE_TRADE_ORDER_MANAGE",
                    "ROLE_TRADE_ORDER_DELETE",
                    "ROLE_TRADE_ORDER_DEAL"
                ]
            }
        },
        {
            // 保留 Vue 独立审核页的快照、同意/拒绝和驳回原因契约，支持历史直达地址。
            path: "tradeOrder/audit",
            element: <TradeOrderAuditPage />,
            handle: {
                title: "审核补录订单",
                permissions: [
                    "ROLE_TRADE_ORDER_R",
                    "ROLE_TRADE_ORDER_MANAGE",
                    "ROLE_TRADE_ORDER_DELETE",
                    "ROLE_TRADE_ORDER_DEAL"
                ]
            }
        },
        {
            // Vue 交易订单下的提货详情使用交易订单权限，不能复用采购管理的 ROLE_PROJECT_R 守卫。
            path: "delivery/detail",
            element: <TradeOrderDeliveryDetailPage />,
            handle: {
                title: "提货详情",
                permissions: [
                    "ROLE_TRADE_ORDER_R",
                    "ROLE_TRADE_ORDER_MANAGE",
                    "ROLE_TRADE_ORDER_DELETE",
                    "ROLE_TRADE_ORDER_DEAL"
                ]
            }
        },
        {
            path: "zoneAccountManagement",
            element: <ZoneAccountPage />,
            handle: { title: "专区余额管理", permissions: ["ROLE_ZONE_ACCOUNT_R", "ROLE_ZONE_ACCOUNT_MANAGE"] }
        },
        {
            path: "rechargeDetails/:id",
            element: <ZoneRechargeDetailPage />,
            handle: { title: "充值明细详情", permissions: ["ROLE_ZONE_ACCOUNT_R", "ROLE_ZONE_ACCOUNT_MANAGE"] }
        },
        {
            path: "zoneReservationOrder",
            element: <ZoneReservationOrderPage />,
            handle: { title: "专区预约单", permissions: ["ROLE_ZONE_PREORDER_R", "ROLE_ZONE_PREORDER_MANAGE"] }
        },
        {
            // 保留 Vue 隐藏详情入口，详情编号通过 query.id 传递。
            path: "zoneReservationOrderDetail",
            element: <ZoneReservationOrderDetailPage />,
            handle: { title: "预约单详情", permissions: ["ROLE_ZONE_PREORDER_R", "ROLE_ZONE_PREORDER_MANAGE"] }
        },
        {
            // 缺陷单 12596 要求编辑采用独立页面，预约单编号继续通过 query.id 传递。
            path: "zoneReservationOrderEdit",
            element: <ZoneReservationOrderEditPage />,
            handle: {
                title: "编辑预约单",
                activeMenu: "/tradeManage/zoneReservationOrder",
                // 合同详情预约明细也可直达编辑页，不能只允许预约单菜单权限。
                permissions: ["ROLE_ZONE_PREORDER_R", "ROLE_ZONE_PREORDER_MANAGE", "ROLE_ZONE_CONTRACT_DEAL"]
            }
        },
        {
            // 沿用 Vue 专区合同列表和查看、处理、管理权限。
            path: "zoneContract",
            element: <ZoneContractPage />,
            handle: { title: "专区合同", permissions: zoneContractPermissions }
        },
        {
            path: "zoneContract/add",
            element: <ZoneContractFormPage />,
            handle: { title: "新增合同", permissions: zoneContractPermissions }
        },
        {
            path: "zoneContract/edit/:id",
            element: <ZoneContractFormPage />,
            handle: { title: "编辑合同", permissions: zoneContractPermissions }
        },
        {
            path: "zoneContractDetail",
            element: <ZoneContractDetailPage />,
            handle: { title: "合同详情", permissions: zoneContractPermissions }
        }
    ]
};

export default tradeManageRoutes;
